import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AmountFilterOperation } from 'constants/common';
import CustomAmountFilter, { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { TipInvoiceFilter } from 'containers/home/components/constants';
import { TypeFilters } from '../constants';
import { ReceiptStatus } from '../receipts.model';
import { useLazyGetAllCustomersQuery } from 'containers/invoices/api';
import { useLazyGetMerchantsByTeamIdReceiptsQuery } from '../api';
import { getPaymentMethodFilters } from 'containers/invoices/utils';
import { getReceiptStatusFilter } from '../util';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import DateUtils from 'utils/dateUtils';
import { getAmountQueryParam } from 'containers/dispatch/utils';
import { joinTextArray } from 'utils/commonUtils';
import { pluckValuesFromArrayWithSpecificKey } from 'utils/formatters';

interface ReceiptsFilterProps {
  onFiltersChange: (filters: Record<string, unknown>) => void;
}

const ReceiptsFilter: FC<ReceiptsFilterProps> = memo(({ onFiltersChange }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedCustomersDraft, setSelectedCustomersDraft] = useState<string[]>([]);
  const [customerSearchString, setCustomerSearchString] = useState('');

  const [selectedStatuses, setSelectedStatuses] = useState<ReceiptStatus[]>([]);
  const [selectedStatusesDraft, setSelectedStatusesDraft] = useState<string[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [selectedMerchantsDraft, setSelectedMerchantsDraft] = useState<string[]>([]);
  const [merchantSearchString, setMerchantSearchString] = useState('');

  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [paymentMethodsDraft, setPaymentMethodsDraft] = useState<string[]>([]);

  const [types, setTypes] = useState<string[]>([]);
  const [typesDraft, setTypesDraft] = useState<string[]>([]);

  const [tipIncluded, setTipIncluded] = useState<string[]>([]);
  const [tipIncludedDraft, setTipIncludedDraft] = useState<string[]>([]);

  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });

  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdReceiptsQuery();

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;

  const customerOptions = useMemo(() => getCustomersResponse?.customers || [], [getCustomersResponse]);
  const merchantOptions = useMemo(() => getMerchantsResponse || [], [getMerchantsResponse]);
  const paymentMethodFilters = getPaymentMethodFilters() || [];
  const receiptStatusFilters = getReceiptStatusFilter() || [];

  const previousTeamIdRef = useRef<number | undefined>(currentTeam?.id);
  const isInitialMountRef = useRef<boolean>(true);

  useEffect(() => {
    if (currentTeam?.id) {
      getCustomersQuery({ teamId: currentTeam.id });
      getMerchantsQuery({ teamId: currentTeam.id });
    }
  }, [currentTeam?.id, getCustomersQuery, getMerchantsQuery]);

  useEffect(() => {
    if (currentTeam?.id && previousTeamIdRef.current !== currentTeam?.id && previousTeamIdRef.current !== undefined) {
      setSelectedCustomers([]);
      setSelectedCustomersDraft([]);
      setCustomerSearchString('');
      setSelectedStatuses([]);
      setSelectedStatusesDraft([]);
      setStartDate(null);
      setEndDate(null);
      setSelectedMerchants([]);
      setSelectedMerchantsDraft([]);
      setMerchantSearchString('');
      setPaymentMethods([]);
      setPaymentMethodsDraft([]);
      setTypes([]);
      setTypesDraft([]);
      setTipIncluded([]);
      setTipIncludedDraft([]);
      setAmountFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    previousTeamIdRef.current = currentTeam?.id;
  }, [currentTeam?.id]);

  const getTextForFilter = useCallback(
    (filter: string) => {
      switch (filter) {
        case 'customers':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              customerOptions.filter(item => selectedCustomers.includes(item['ID'])),
              'name',
              'lastName'
            )
          );
        case 'merchants':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              merchantOptions.filter(item => selectedMerchants.includes(item['merchantId'])),
              'firstName',
              'lastName'
            )
          );
        case 'statuses':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              receiptStatusFilters.filter(item => selectedStatuses.includes(item.key as ReceiptStatus)),
              'value'
            )
          );
        case 'paymentMethods':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              paymentMethodFilters.filter(item => paymentMethods.includes(item.key)),
              'value'
            )
          );
        case 'tipIncluded':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              TipInvoiceFilter.filter(item => tipIncluded.includes(item.key)),
              'value'
            )
          );
        case 'types':
          return joinTextArray(
            pluckValuesFromArrayWithSpecificKey(
              TypeFilters.filter(item => types.includes(item.key)),
              'value'
            )
          );
        default:
          return '';
      }
    },
    [
      customerOptions,
      merchantOptions,
      paymentMethodFilters,
      receiptStatusFilters,
      selectedCustomers,
      selectedMerchants,
      selectedStatuses,
      paymentMethods,
      tipIncluded,
      types
    ]
  );

  useEffect(() => {
    const filters = {
      customerId: selectedCustomers.length > 0 ? selectedCustomers : [],
      createdBy: selectedMerchants.length > 0 ? selectedMerchants : [],
      paymentMethod: paymentMethods.length > 0 ? paymentMethods : [],
      tipAmountNE: tipIncluded.length ? 0 : null,
      sourceType: types.length > 0 ? types : [],
      status: selectedStatuses.length > 0 ? selectedStatuses : [],
      createdAtFrom: DateUtils.ensureUTCStartDateFormat(startDate) || null,
      createdAtTo: DateUtils.ensureUTCEndDateFormat(endDate) || null,
      amount: getAmountQueryParam(amountFilter) || null
    };

    onFiltersChange(filters);

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }
  }, [
    selectedCustomers,
    selectedMerchants,
    paymentMethods,
    tipIncluded,
    types,
    selectedStatuses,
    startDate,
    endDate,
    amountFilter,
    onFiltersChange
  ]);

  const handleClearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <div className="flex h-full w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-2">
      <DropdownWithSearch
        filterId="receipts-status-filter"
        data={receiptStatusFilters}
        selectedDraftArray={selectedStatusesDraft}
        setSelectedDraftArray={setSelectedStatusesDraft}
        selectedArray={selectedStatuses}
        setSelectedArray={setSelectedStatuses as (value: string[]) => void}
        dataKey="value"
        dataIdKey="key"
        buttonPrimaryText="Status"
        buttonSecondaryText={getTextForFilter('statuses')}
        filterPlaceholderText="Filter by status"
        allowSearch={false}
      />

      <CustomDateRangePicker
        filterId="receipts-date-filter"
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        placeholder="Date range"
        onClear={handleClearDateRange}
      />

      <DropdownWithSearch
        searchFilterId="receipts-merchant-name-search-field"
        filterId="receipts-createdby-filter"
        data={merchantOptions}
        searchText={merchantSearchString}
        setSearchText={setMerchantSearchString}
        selectedDraftArray={selectedMerchantsDraft}
        setSelectedDraftArray={setSelectedMerchantsDraft}
        selectedArray={selectedMerchants}
        setSelectedArray={setSelectedMerchants}
        dataKey="firstName"
        secondaryDataKey="lastName"
        dataIdKey="merchantId"
        buttonPrimaryText="Created by"
        buttonSecondaryText={getTextForFilter('merchants')}
        filterPlaceholderText="Filter by merchants"
        searchPlaceHolderText="Search merchant names"
        allowSearch={true}
      />

      <DropdownWithSearch
        searchFilterId="receipts-customer-name-search-field"
        filterId="receipts-customer-filter"
        data={customerOptions}
        searchText={customerSearchString}
        setSearchText={setCustomerSearchString}
        selectedDraftArray={selectedCustomersDraft}
        setSelectedDraftArray={setSelectedCustomersDraft}
        selectedArray={selectedCustomers}
        setSelectedArray={setSelectedCustomers}
        dataKey="name"
        secondaryDataKey="lastName"
        dataIdKey="ID"
        buttonPrimaryText="Customers"
        buttonSecondaryText={getTextForFilter('customers')}
        filterPlaceholderText="Filter by customers"
        searchPlaceHolderText="Search customer names"
        allowSearch={true}
      />

      <DropdownWithSearch
        filterId="receipts-payment-method-filter"
        data={paymentMethodFilters}
        selectedDraftArray={paymentMethodsDraft}
        setSelectedDraftArray={setPaymentMethodsDraft}
        selectedArray={paymentMethods}
        setSelectedArray={setPaymentMethods}
        dataKey="value"
        dataIdKey="key"
        buttonPrimaryText="Payment method"
        buttonSecondaryText={getTextForFilter('paymentMethods')}
        filterPlaceholderText="Filter by payment method"
        allowSearch={false}
      />

      <DropdownWithSearch
        filterId="receipts-type-filter"
        data={TypeFilters}
        selectedDraftArray={typesDraft}
        setSelectedDraftArray={setTypesDraft}
        selectedArray={types}
        setSelectedArray={setTypes}
        dataKey="value"
        dataIdKey="key"
        buttonPrimaryText="Type"
        buttonSecondaryText={getTextForFilter('types')}
        filterPlaceholderText="Filter by type"
        allowSearch={false}
      />

      <CustomAmountFilter amountFilter={amountFilter} setAmountFilter={setAmountFilter} title="Amount" />

      <DropdownWithSearch
        filterId="receipts-tip-collected-filter"
        data={TipInvoiceFilter}
        selectedDraftArray={tipIncludedDraft}
        setSelectedDraftArray={setTipIncludedDraft}
        selectedArray={tipIncluded}
        setSelectedArray={setTipIncluded}
        dataKey="value"
        dataIdKey="key"
        buttonPrimaryText="Tip"
        buttonSecondaryText={getTextForFilter('tipIncluded')}
        filterPlaceholderText="Filter by tip collected"
        allowSearch={false}
        showResultsCount={false}
        alignRight
      />
    </div>
  );
});

ReceiptsFilter.displayName = 'ReceiptsFilter';

export default ReceiptsFilter;
